import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";

interface MonthlyStats {
  name: string;
  items: number;
  users: number;
}

interface LeadsBySource {
  source: string;
  count: number;
}

export default function StatsPage() {
  const [loading, setLoading] = useState(true);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [leadsBySource, setLeadsBySource] = useState<LeadsBySource[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const stats: MonthlyStats[] = [];
      const today = new Date();

      // Get stats for the last 6 months
      for (let i = 0; i < 6; i++) {
        const currentMonth = subMonths(today, i);
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);

        // Get users created in this month
        let usersQuery = supabase
          .from('users')
          .select('id, niche_type', { count: 'exact', head: true })
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString());

        // If partner, only count users they created
        if (user?.role === 'parceiro') {
          usersQuery = usersQuery.eq('created_by', user.id);
        }

        const { count: usersCount, data: usersData } = await usersQuery;

        // Count items for each niche type
        let totalItems = 0;

        for (const userData of usersData || []) {
          let itemCount = 0;
          
          switch (userData.niche_type) {
            case 'imoveis':
              const { count: propertiesCount } = await supabase
                .from('properties')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', userData.id)
                .gte('created_at', start.toISOString())
                .lte('created_at', end.toISOString());
              itemCount = propertiesCount || 0;
              break;
              
            case 'veiculos':
              const { count: carsCount } = await supabase
                .from('cars')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', userData.id)
                .gte('created_at', start.toISOString())
                .lte('created_at', end.toISOString());
              itemCount = carsCount || 0;
              break;
              
            case 'diversos':
              const { count: productsCount } = await supabase
                .from('products')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', userData.id)
                .gte('created_at', start.toISOString())
                .lte('created_at', end.toISOString());
              itemCount = productsCount || 0;
              break;
          }
          
          totalItems += itemCount;
        }

        stats.unshift({
          name: format(currentMonth, 'MMM', { locale: ptBR }),
          items: totalItems,
          users: usersCount || 0,
        });
      }

      setMonthlyStats(stats);

      // Fetch leads by source for all niches
      const { data: userIds } = await supabase
        .from('users')
        .select('id')
        .eq(user?.role === 'parceiro' ? 'created_by' : 'id', user?.id);

      if (userIds && userIds.length > 0) {
        const { data: leadSourceData } = await supabase
          .from('leads')
          .select('source')
          .in('property_id', userIds.map(u => u.id));

        const sourceCount = leadSourceData?.reduce((acc, curr) => {
          acc[curr.source] = (acc[curr.source] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        setLeadsBySource(
          Object.entries(sourceCount || {}).map(([source, count]) => ({
            source,
            count,
          }))
        );
      }

    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Estatísticas</h1>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Crescimento Mensal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="items"
                    stroke="#8884d8"
                    name="Itens"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#82ca9d"
                    name="Usuários"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leads por Origem</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadsBySource}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="source" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" name="Leads" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}